"use client"

import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"

import {
  type InviteTokenRecord,
  UserAccessScope,
  UserStatus,
  type CreateUserInput,
  type SendInviteInput,
  type SystemRole,
  type UpdateUserInput,
  type User,
} from "@/features/users/model"

function createId(prefix: string) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`
}

type UsersState = {
  users: User[]
  inviteTokens: InviteTokenRecord[]
  isLoading: boolean
  error: string | null
  getById: (id: string) => User | undefined
  createUser: (input: CreateUserInput) => Promise<User>
  updateUser: (id: string, input: UpdateUserInput) => Promise<User>
  sendInvite: (input: SendInviteInput) => Promise<InviteTokenRecord>
  setUserStatus: (id: string, status: UserStatus) => Promise<User>
  resendInvite: (id: string) => Promise<User>
  setUserRoles: (id: string, roles: SystemRole[]) => Promise<User>
  setUserAccessScope: (
    id: string,
    accessScope: UserAccessScope,
    managedBranchId?: string | null
  ) => Promise<User>
}

function createInviteToken() {
  return `inv_${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`
}

function requiresManagedBranch(accessScope: UserAccessScope) {
  return accessScope === UserAccessScope.BRANCH || accessScope === UserAccessScope.PARISH_AND_BRANCH
}

export const useUsersStore = create<UsersState>()(
  persist(
    (set, get) => ({
  users: [],
  inviteTokens: [],
  isLoading: false,
  error: null,

  getById: (id) => get().users.find((user) => user.id === id),

  createUser: async (input) => {
    set({ isLoading: true, error: null })
    const memberAlreadyLinked = get().users.some((user) => user.memberId === input.memberId)
    if (memberAlreadyLinked) {
      set({ isLoading: false, error: "users.errors.memberAlreadyLinked" })
      throw new Error("users.errors.memberAlreadyLinked")
    }

    const normalizedEmail = input.email.trim().toLowerCase()
    const duplicate = get().users.some((user) => user.email.toLowerCase() === normalizedEmail)
    if (duplicate) {
      set({ isLoading: false, error: "users.errors.emailAlreadyUsed" })
      throw new Error("users.errors.emailAlreadyUsed")
    }
    if (requiresManagedBranch(input.accessScope) && !input.managedBranchId) {
      set({ isLoading: false, error: "users.errors.branchScopeRequired" })
      throw new Error("users.errors.branchScopeRequired")
    }

    const now = new Date().toISOString()
    const user: User = {
      id: createId("usr"),
      memberId: input.memberId,
      email: normalizedEmail,
      firstName: input.firstName,
      lastName: input.lastName,
      roles: input.roles,
      accessScope: input.accessScope,
      managedBranchId: requiresManagedBranch(input.accessScope) ? input.managedBranchId ?? null : null,
      status: UserStatus.ACTIVE,
      createdAt: now,
      updatedAt: now,
    }

    set((state) => ({
      users: [user, ...state.users],
      isLoading: false,
      error: null,
    }))
    return user
  },

  sendInvite: async (input) => {
    set({ isLoading: true, error: null })
    const user = get().users.find((entry) => entry.id === input.userId)
    if (!user) {
      set({ isLoading: false, error: "users.errors.notFound" })
      throw new Error("users.errors.notFound")
    }

    const invitedAt = new Date().toISOString()
    const expiresAt = new Date(Date.now() + 1000 * 60 * 60 * 24 * 7).toISOString()
    const tokenRecord: InviteTokenRecord = {
      id: createId("uit"),
      token: createInviteToken(),
      userId: user.id,
      expiresAt,
      createdAt: invitedAt,
    }

    const updatedUser: User = {
      ...user,
      status: UserStatus.INVITED,
      invitedAt,
      updatedAt: invitedAt,
    }

    set((state) => ({
      users: state.users.map((entry) => (entry.id === user.id ? updatedUser : entry)),
      inviteTokens: [tokenRecord, ...state.inviteTokens],
      isLoading: false,
      error: null,
    }))

    return tokenRecord
  },

  updateUser: async (id, input) => {
    set({ isLoading: true, error: null })
    const existing = get().users.find((user) => user.id === id)
    if (!existing) {
      set({ isLoading: false, error: "users.errors.notFound" })
      throw new Error("users.errors.notFound")
    }
    if (requiresManagedBranch(input.accessScope) && !input.managedBranchId) {
      set({ isLoading: false, error: "users.errors.branchScopeRequired" })
      throw new Error("users.errors.branchScopeRequired")
    }

    const updated: User = {
      ...existing,
      roles: input.roles,
      accessScope: input.accessScope,
      managedBranchId: requiresManagedBranch(input.accessScope) ? input.managedBranchId ?? null : null,
      status: input.status,
      updatedAt: new Date().toISOString(),
    }

    set((state) => ({
      users: state.users.map((user) => (user.id === id ? updated : user)),
      isLoading: false,
      error: null,
    }))
    return updated
  },

  setUserStatus: async (id, status) => {
    const existing = get().users.find((user) => user.id === id)
    if (!existing) {
      throw new Error("users.errors.notFound")
    }
    return get().updateUser(id, {
      roles: existing.roles,
      accessScope: existing.accessScope,
      managedBranchId: existing.managedBranchId ?? null,
      status,
    })
  },

  resendInvite: async (id) => {
    const existing = get().getById(id)
    if (!existing) {
      throw new Error("users.errors.notFound")
    }

    await get().sendInvite({ userId: existing.id })
    const updated = get().getById(id)
    if (!updated) {
      throw new Error("users.errors.notFound")
    }
    return updated
  },

  setUserRoles: async (id, roles) => {
    const existing = get().users.find((user) => user.id === id)
    if (!existing) {
      throw new Error("users.errors.notFound")
    }
    return get().updateUser(id, {
      roles,
      accessScope: existing.accessScope,
      managedBranchId: existing.managedBranchId ?? null,
      status: existing.status,
    })
  },

  setUserAccessScope: async (id, accessScope, managedBranchId) => {
    const existing = get().users.find((user) => user.id === id)
    if (!existing) {
      throw new Error("users.errors.notFound")
    }
    return get().updateUser(id, {
      roles: existing.roles,
      accessScope,
      managedBranchId: requiresManagedBranch(accessScope) ? managedBranchId ?? null : null,
      status: existing.status,
    })
  },
    }),
    {
      name: "users-store-v1",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        users: state.users,
        inviteTokens: state.inviteTokens,
      }),
    }
  )
)
